import { LoadingState } from '../ui';
// src/pages/Terms.jsx

import React, { useState, useEffect, useMemo } from "react";
import DOMPurify from "dompurify";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import notify from "../lib/notify";
import SidebarNavbar from '../components/SidebarNavbar';
import BottomNavbar from '../components/BottomNavbar';
import { Button as FcButton, Card as FcCard } from '../ui';

export default function Terms({ session, userRole, username }) {
  const navigate = useNavigate();

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };
  const [termsDoc, setTermsDoc] = useState(null);
  const [hasAccepted, setHasAccepted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);

  const sanitizedContent = useMemo(() => {
    if (!termsDoc?.content) return "";
    return DOMPurify.sanitize(termsDoc.content, { USE_PROFILES: { html: true } });
  }, [termsDoc?.content]);

  useEffect(() => {
    fetchTermsAndAcceptance();
  }, [session]);

  const fetchTermsAndAcceptance = async () => {
    try {
      // Fetch active terms document
      const { data: docs, error: docError } = await supabase
        .rpc('get_active_legal_document', { doc_type: 'terms' });

      if (docError) throw docError;

      if (docs && docs.length > 0) {
        setTermsDoc(docs[0]);

        // Check if user has accepted this version
        if (session?.user?.id) {
          const { data: acceptance, error: acceptError } = await supabase
            .from('user_legal_acceptances')
            .select('*')
            .eq('user_id', session.user.id)
            .eq('document_id', docs[0].id)
            .single();

          if (!acceptError && acceptance) {
            setHasAccepted(true);
          }
        }
      }
    } catch (error) {
      notify('Failed to load terms of service', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptTerms = async () => {
    if (!session?.user?.id || !termsDoc) return;

    setAccepting(true);
    try {
      await supabase.rpc('record_legal_acceptance', { doc_id: termsDoc.id });
      
      setHasAccepted(true);
      notify('Thank you for accepting our Terms of Service', 'success');
    } catch (error) {
      notify('Failed to record acceptance', 'error');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-canvas">
        <div className="block md:hidden w-full bg-ui-green py-3 px-4 sticky top-0 z-30">
          <span className="text-white text-lg font-semibold">Footy Coach</span>
        </div>
        <SidebarNavbar handleLogout={handleLogout} userRole={userRole} />
        <BottomNavbar handleLogout={handleLogout} userRole={userRole} />
        <div role="main" id="fc-main-content" className="md:ml-64 pt-8 pb-24 px-4 sm:px-6 lg:px-8 fc-workspace">
          <div className="max-w-3xl mx-auto text-center py-12">
            <LoadingState label="Loading terms of service..." />
          </div>
        </div>
      </div>
    );
  }

  if (!termsDoc) {
    return (
      <div className="min-h-screen bg-surface-canvas">
        <div className="block md:hidden w-full bg-ui-green py-3 px-4 sticky top-0 z-30">
          <span className="text-white text-lg font-semibold">Footy Coach</span>
        </div>
        <SidebarNavbar handleLogout={handleLogout} userRole={userRole} />
        <BottomNavbar handleLogout={handleLogout} userRole={userRole} />
        <div role="main" id="fc-main-content" className="md:ml-64 pt-8 pb-24 px-4 sm:px-6 lg:px-8 fc-workspace">
          <div className="max-w-3xl mx-auto">
            <FcCard as="div" padding="standard" className="bg-surface-card border border-ui-border rounded-2xl shadow-sm p-8 text-center">
              <h1 className="text-3xl font-extrabold text-ui-green mb-4">Terms and Conditions</h1>
              <p className="text-content-secondary">No active terms of service found. Please contact support.</p>
            </FcCard>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-canvas">
      <div className="block md:hidden w-full bg-ui-green py-3 px-4 sticky top-0 z-30">
        <span className="text-white text-lg font-semibold">Footy Coach</span>
      </div>
      <SidebarNavbar handleLogout={handleLogout} userRole={userRole} />
      <BottomNavbar handleLogout={handleLogout} userRole={userRole} />
      <div role="main" id="fc-main-content" className="md:ml-64 pt-8 pb-24 px-4 sm:px-6 lg:px-8 fc-workspace">
        <div className="max-w-3xl mx-auto">
          <FcCard as="div" padding="standard" className="bg-surface-card border border-ui-border rounded-2xl shadow-sm p-8">
            <h1 className="text-3xl sm:text-4xl font-extrabold text-ui-green">{termsDoc.title}</h1>

            <div className="prose max-w-none text-content-primary">
              <div className="mb-6 text-sm text-content-secondary">
                <p><strong>Version:</strong> {termsDoc.version}</p>
                <p><strong>Effective Date:</strong> {new Date(termsDoc.effective_date).toLocaleDateString('en-GB')}</p>
                {termsDoc.updated_at && termsDoc.created_at !== termsDoc.updated_at && (
                  <p><strong>Last Updated:</strong> {new Date(termsDoc.updated_at).toLocaleDateString('en-GB')}</p>
                )}
              </div>

              {/* Render HTML content */}
              <div dangerouslySetInnerHTML={{ __html: sanitizedContent }} />

              {/* Acceptance tracking for logged-in users */}
              {session?.user?.id && (
                <div className="mt-8 pt-6 border-t border-ui-border">
                  {hasAccepted ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="text-ui-success font-semibold flex items-center gap-2">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        You have accepted these Terms of Service
                      </p>
                    </div>
                  ) : (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-yellow-800 mb-3">
                        By clicking "I Accept" below, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
                      </p>
                      <FcButton variant="primary" type="submit"
                        onClick={handleAcceptTerms}
                        disabled={accepting}
                        className="disabled:cursor-not-allowed"
                      >
                        {accepting ? 'Recording...' : 'I Accept the Terms of Service'}
                      </FcButton>
                    </div>
                  )}
                </div>
              )}
            </div>
          </FcCard>
        </div>
      </div>
    </div>
  );
}
